import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { useI18n } from "../i18n";
import { useMediaQuery, usePrefersReducedMotion } from "../lib/hooks";

/**
 * Highlight Animation (วิดีโอ 15 วินาที จาก design/animation)
 * - ต่ำกว่า 768px ใช้ไฟล์ 9:16 ; ตั้งแต่ 768px ขึ้นไปใช้ไฟล์ 16:9 เต็มความกว้างเวที
 * - เลือกไฟล์ด้วย matchMedia (ไม่โหลดสองไฟล์) และ load() ใหม่เมื่อข้ามจุดตัด
 * - เล่นอัตโนมัติแบบไม่มีเสียงเมื่อเข้าจอ และหยุดเมื่อออกจอ ; prefers-reduced-motion ไม่เล่นเอง (ผู้ใช้กดเล่นได้)
 * - มีปุ่มหยุด/เล่นเสมอ (WCAG 2.2.2) ; preload=none เพื่อไม่โหลดวิดีโอจนกว่าจะเลื่อนมาถึง
 */
const TALL = { base: "/media/highlight-720", poster: "/media/highlight-poster.jpg" };
const WIDE = { base: "/media/highlight-wide-1280", poster: "/media/highlight-wide-poster.jpg" };

export function HighlightAnimation() {
  const { t } = useI18n();
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLVideoElement>(null);
  const wide = useMediaQuery("(min-width: 768px)");
  const media = wide ? WIDE : TALL;
  const [playing, setPlaying] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  // poster อยู่ใต้หน้าจอแรก: ตั้งค่า poster เมื่อเลื่อนใกล้ถึงเท่านั้น ไม่แย่งแบนด์วิดท์กับภาพและฟอนต์ที่เห็นก่อน (กรอบมี aspect-ratio ล็อกไว้แล้ว จึงไม่ทำให้เลย์เอาต์ขยับ)
  const frame = useRef<HTMLElement>(null);
  const [near, setNear] = useState(typeof IntersectionObserver === "undefined");

  useEffect(() => {
    const el = frame.current;
    if (!el || near) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  useEffect(() => {
    const v = ref.current;
    if (!v || reduced || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e) return;
        if (e.isIntersecting && !userPaused) void v.play().catch(() => undefined);
        else v.pause();
      },
      { threshold: 0.5 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [reduced, userPaused]);

  // ข้ามจุดตัดแล้ว source เปลี่ยน ต้อง load() ใหม่ (ถ้ากำลังเล่นอยู่ให้เล่นต่อ)
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const v = ref.current;
    if (!v) return;
    const wasPlaying = !v.paused;
    v.load();
    if (wasPlaying) void v.play().catch(() => undefined);
  }, [wide]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      setUserPaused(false);
      void v.play().catch(() => undefined);
    } else {
      setUserPaused(true);
      v.pause();
    }
  };

  return (
    <div className="hl-stage">
      <figure ref={frame} className={`hl-frame ${wide ? "is-wide" : "is-tall"}`}>
        <video
          ref={ref}
          className="hl-video"
          poster={near ? media.poster : undefined}
          width={wide ? 1280 : 720}
          height={wide ? 720 : 1280}
          muted
          loop
          playsInline
          preload="none"
          aria-label={t("web.hl.label")}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        >
          <source src={`${media.base}.webm`} type="video/webm" />
          <source src={`${media.base}.mp4`} type="video/mp4" />
        </video>
        <button type="button" className="hl-toggle" onClick={toggle} aria-label={playing ? t("web.hl.pause") : t("web.hl.play")} title={playing ? t("web.hl.pause") : t("web.hl.play")}>
          <Icon name={playing ? "pause" : "play"} />
        </button>
      </figure>
    </div>
  );
}
